import React, { useEffect, useState } from 'react';
import { io, Socket, SocketOptions, ManagerOptions } from 'socket.io-client';
import { useLocation } from 'react-router-dom';
import qs from 'qs';
import { IChatMessage, IChatThread } from '../../libs/chat';
import { useMetaData } from '../../contexts/metadata';

export enum EventNames {
  ENTER_PATIENT = 'chat_enter_pt',
  ENTER_USER = 'chat_enter_user',
  ENTER_SUCCESS = 'chat_enter_success',
  ENTER_FAIL = 'chat_enter_fail',
  LIST_MESSAGES = 'chat_list_msgs',
  LIST_MESSAGES_SUCCESS = 'chat_list_msgs_success',
  LIST_MESSAGES_FAIL = 'chat_list_msgs_fail',
  NEW_MESSAGE_TO_PATIENT = 'chat_msg_to_pt',
  NEW_MESSAGE_TO_USER = 'chat_msg_to_user',
  NEW_MESSAGE_FAIL = 'chat_new_msg_fail',
  NEW_MESSAGE = 'chat_new_msg',
  LEAVE_PATIENT = 'chat_leave_pt',
  LEAVE_USER = 'chat_leave_user',
  LEAVE_SUCCESS = 'chat_leave_success',
  NEW_UNREAD_FOR_PATIENT = 'chat_new_unread',
  MARK_THREAD_READ = 'chat_mark_thread_read',
  THREAD_READ = 'chat_thread_read'
}

interface ListenEventsMap {
  [EventNames.ENTER_SUCCESS]: () => void;
  [EventNames.ENTER_FAIL]: (error: string) => void;
  [EventNames.LIST_MESSAGES_SUCCESS]: (message: IChatMessage[]) => void;
  [EventNames.LIST_MESSAGES_FAIL]: (error: string) => void;
  [EventNames.NEW_UNREAD_FOR_PATIENT]: (
    patientId: number,
    thread: IChatThread
  ) => void;
  [EventNames.NEW_MESSAGE_FAIL]: (error: string) => void;
  [EventNames.NEW_MESSAGE_TO_PATIENT]: (payload: any) => void;
  [EventNames.THREAD_READ]: (threadId: number, readAt: Date) => void;
}

interface EmitEventsMap {
  [EventNames.ENTER_PATIENT]: () => void;
  [EventNames.ENTER_USER]: (patientIds: number[]) => void;
  [EventNames.LEAVE_PATIENT]: () => void;
  [EventNames.LEAVE_USER]: (patientIds: number[]) => void;
  [EventNames.LIST_MESSAGES]: (
    patientId: number,
    limit: number,
    cutoff?: string
  ) => void;
  [EventNames.NEW_MESSAGE_TO_PATIENT]: (payload: any) => void;
  [EventNames.NEW_MESSAGE_TO_USER]: (payload: any) => void;
  [EventNames.MARK_THREAD_READ]: (threadId: number) => void;
}

export type ChatSocket = Socket<ListenEventsMap, EmitEventsMap>;

const ChatSocketContext = React.createContext<ChatSocket>(null);

export const ChatSocketProvider: React.FC = (props) => {
  const { account } = useMetaData();
  const [socket, setSocket] = useState(null);
  const { search } = useLocation();
  const query = qs.parse(search, {
    ignoreQueryPrefix: true
  });
  const token = query && query.t ? query.t : null;
  let isChatEnabled = false;
  if (account?.isChatEnabled) {
    isChatEnabled = account.isChatEnabled;
  }
  const location = useLocation();
  const ptTest = '/chat/pt/';
  const isPatientFacing = location.pathname.slice(0, ptTest.length) === ptTest;

  useEffect(() => {
    if (!isPatientFacing && !isChatEnabled) {
      return;
    }
    const config: Partial<ManagerOptions & SocketOptions> = {
      autoConnect: false,
      transports: ['websocket']
    };
    if (query && query.t) {
      config.auth = { token };
    }
    const socket: ChatSocket = io(config);
    socket.connect();
    setSocket(socket);
    return () => {
      socket.disconnect();
    };
  }, [token, isChatEnabled, isPatientFacing]);

  return <ChatSocketContext.Provider value={socket} {...props} />;
};

export function useChatSocket(): ChatSocket {
  const context = React.useContext(ChatSocketContext);
  if (context === undefined) {
    throw new Error('useChatSocket must be used within a ChatSocketProvider');
  }
  return context;
}
